<template>
    <div>
      <Header />
  
      <!-- -- Page Header -- -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Employees</h5>
          <p class="page-sub">Overview of your workforce</p>
        </div>
        <div class="page-actions">
          <select class="filter-select" v-model="selectedMonth" @change="fetchAll">
            <option v-for="m in months" :key="m.value" :value="m.value">{{ m.label }}</option>
          </select>
          <router-link :to="$url('/create-employee')" class="btn-primary-action">
            <font-awesome-icon icon="plus" /> New Employee
          </router-link>
        </div>
      </div>
  
      <!-- -- Loading -- -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted">Loading dashboard...</p>
      </div>
  
      <template v-else>
  
        <!-- ══════════════════════════════
             STATUS CARDS - row 1
        ══════════════════════════════ -->
        <div class="stat-grid mb-4">
          <div class="stat-card stat-card--green">
            <div class="stat-icon stat-icon--green"><font-awesome-icon icon="users" /></div>
            <div>
              <p class="stat-label">Active</p>
              <p class="stat-value">{{ stats.active }}</p>
              <p class="stat-sub">{{ pct(stats.active) }}% of total</p>
            </div>
          </div>
          <div class="stat-card stat-card--purple">
            <div class="stat-icon stat-icon--purple"><font-awesome-icon icon="umbrella-beach" /></div>
            <div>
              <p class="stat-label">On Leave</p>
              <p class="stat-value">{{ stats.on_leave }}</p>
              <p class="stat-sub">{{ pct(stats.on_leave) }}% of total</p>
            </div>
          </div>
          <div class="stat-card stat-card--amber">
            <div class="stat-icon stat-icon--amber"><font-awesome-icon icon="pause-circle" /></div>
            <div>
              <p class="stat-label">Suspended</p>
              <p class="stat-value">{{ stats.suspended }}</p>
              <p class="stat-sub">{{ pct(stats.suspended) }}% of total</p>
            </div>
          </div>
          <div class="stat-card stat-card--red">
            <div class="stat-icon stat-icon--red"><font-awesome-icon icon="user-slash" /></div>
            <div>
              <p class="stat-label">Terminated</p>
              <p class="stat-value">{{ stats.terminated }}</p>
              <p class="stat-sub">{{ pct(stats.terminated) }}% of total</p>
            </div>
          </div>
        </div>
  
        <!-- STATUS CARDS - row 2 -->
        <div class="stat-grid mb-5">
          <div class="stat-card stat-card--blue">
            <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="envelope" /></div>
            <div>
              <p class="stat-label">Invited</p>
              <p class="stat-value">{{ stats.invited }}</p>
              <p class="stat-sub">Awaiting signup</p>
            </div>
          </div>
          <div class="stat-card stat-card--gray">
            <div class="stat-icon stat-icon--gray"><font-awesome-icon icon="hourglass-half" /></div>
            <div>
              <p class="stat-label">Pending</p>
              <p class="stat-value">{{ stats.pending }}</p>
              <p class="stat-sub">Awaiting review</p>
            </div>
          </div>
          <div class="stat-card stat-card--red">
            <div class="stat-icon stat-icon--red"><font-awesome-icon icon="sign-out-alt" /></div>
            <div>
              <p class="stat-label">Resigned</p>
              <p class="stat-value">{{ stats.resigned }}</p>
              <p class="stat-sub">This quarter</p>
            </div>
          </div>
          <div class="stat-card stat-card--green">
            <div class="stat-icon stat-icon--green"><font-awesome-icon icon="users-cog" /></div>
            <div>
              <p class="stat-label">Total</p>
              <p class="stat-value">{{ totalEmployees }}</p>
              <p class="stat-sub">All employees</p>
            </div>
          </div>
        </div>
  
        <!-- ══════════════════════════════
             ROW: Status bars + Birthdays
        ══════════════════════════════ -->
        <div class="two-col mb-5">
  
          <!-- Status breakdown -->
          <div class="data-card">
            <div class="card-hd">
              <div>
                <h6 class="card-title">Status breakdown</h6>
                <p class="card-sub">{{ totalEmployees }} employees total</p>
              </div>
            </div>
            <div class="card-body">
              <div class="bar-list">
                <div v-for="s in statusBars" :key="s.key" class="bar-row">
                  <span class="bar-label">{{ s.label }}</span>
                  <div class="bar-track">
                    <div class="bar-fill" :style="{ width: pct(s.count) + '%', background: s.color }"></div>
                  </div>
                  <span class="bar-count">{{ s.count }}</span>
                  <span class="bar-pct">{{ pct(s.count) }}%</span>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Upcoming birthdays -->
          <div class="data-card">
            <div class="card-hd">
              <div>
                <h6 class="card-title">Upcoming birthdays</h6>
                <p class="card-sub">Next 30 days</p>
              </div>
            </div>
            <div class="card-body">
              <div v-if="birthdays.length === 0" class="empty-mini">
                <font-awesome-icon icon="birthday-cake" class="empty-icon" />
                <p>No birthdays in the next 30 days</p>
              </div>
              <div v-else class="bday-list">
                <div v-for="emp in birthdays" :key="emp.id" class="bday-row">
                  <div class="emp-avatar" :style="{ background: emp.avatarBg, color: emp.avatarColor }">
                    {{ emp.initials }}
                  </div>
                  <div class="bday-info">
                    <p class="bday-name">{{ emp.name }}</p>
                    <p class="bday-dept">{{ emp.department }}</p>
                  </div>
                  <div class="bday-date">
                    <span v-if="emp.isToday" class="badge badge--green">Today</span>
                    <span v-else class="bday-date-text">{{ emp.dateLabel }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
        </div>
  
        <!-- ══════════════════════════════
             ROW: Pay grades + Departments
        ══════════════════════════════ -->
        <div class="two-col mb-5">
  
          <!-- Pay grades -->
          <div class="data-card">
            <div class="card-hd">
              <div>
                <h6 class="card-title">Pay grades</h6>
                <p class="card-sub">Headcount per grade</p>
              </div>
            </div>
            <div class="card-body">
              <div v-if="paygrades.length === 0" class="empty-mini">
                <p>No pay grade data available</p>
              </div>
              <div v-else class="bar-list">
                <div v-for="pg in paygrades" :key="pg.id" class="bar-row">
                  <span class="bar-label">{{ pg.name }}</span>
                  <div class="bar-track">
                    <div class="bar-fill" :style="{ width: pgWidth(pg.count) + '%', background: '#7F77DD' }"></div>
                  </div>
                  <span class="bar-count">{{ pg.count }}</span>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Departments -->
          <div class="data-card">
            <div class="card-hd">
              <div>
                <h6 class="card-title">Departments</h6>
                <p class="card-sub">Headcount per department</p>
              </div>
            </div>
            <div class="card-body">
              <div v-if="departments.length === 0" class="empty-mini">
                <p>No department data available</p>
              </div>
              <div v-else class="dept-grid">
                <div v-for="dept in departments" :key="dept.id" class="dept-card">
                  <div class="dept-icon">
                    <font-awesome-icon icon="building" />
                  </div>
                  <div class="dept-info">
                    <p class="dept-name">{{ dept.name }}</p>
                    <p class="dept-count">{{ dept.count }}</p>
                    <p class="dept-sub">employees</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
        </div>
  
        <!-- ══════════════════════════════
             ROW: Headcount trend + Gender
        ══════════════════════════════ -->
        <div class="two-col mb-5">
  
          <!-- Headcount trend -->
          <div class="data-card">
            <div class="card-hd">
              <div>
                <h6 class="card-title">Headcount trend</h6>
                <p class="card-sub">Joined vs left - last 6 months</p>
              </div>
            </div>
            <div class="card-body">
              <div class="chart-legend mb-2">
                <span class="leg-item"><span class="leg-dot" style="background:#378ADD"></span> Joined</span>
                <span class="leg-item"><span class="leg-dot" style="background:#E24B4A"></span> Left</span>
              </div>
              <GChart
                type="ColumnChart"
                :data="trendChartData"
                :options="trendChartOptions"
                style="width:100%;height:220px;"
              />
            </div>
          </div>
  
          <!-- Gender distribution -->
          <div class="data-card">
            <div class="card-hd">
              <div>
                <h6 class="card-title">Gender distribution</h6>
                <p class="card-sub">All employees</p>
              </div>
            </div>
            <div class="card-body gender-body">
              <GChart
                type="PieChart"
                :data="genderChartData"
                :options="genderChartOptions"
                style="width:180px;height:180px;flex-shrink:0;"
              />
              <div class="gender-stats">
                <div class="gender-item">
                  <span class="leg-dot" style="background:#378ADD"></span>
                  <div>
                    <p class="gender-label">Male</p>
                    <p class="gender-val">{{ gender.male }} <span class="gender-pct">{{ genderPct('male') }}%</span></p>
                  </div>
                </div>
                <div class="gender-item">
                  <span class="leg-dot" style="background:#D4537E"></span>
                  <div>
                    <p class="gender-label">Female</p>
                    <p class="gender-val">{{ gender.female }} <span class="gender-pct">{{ genderPct('female') }}%</span></p>
                  </div>
                </div>
                <div class="gender-item">
                  <span class="leg-dot" style="background:#888780"></span>
                  <div>
                    <p class="gender-label">Other</p>
                    <p class="gender-val">{{ gender.other }} <span class="gender-pct">{{ genderPct('other') }}%</span></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
        </div>
  
      </template>
    </div>
  </template>
  
  
  <script>
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { GChart } from 'vue-google-charts';
  
  const AVATAR_PALETTES = [
    { bg: '#E6F1FB', color: '#185FA5' },
    { bg: '#EEEDFE', color: '#3C3489' },
    { bg: '#EAF3DE', color: '#3B6D11' },
    { bg: '#FAEEDA', color: '#854F0B' },
    { bg: '#FBEAF0', color: '#993556' },
    { bg: '#E1F5EE', color: '#0F6E56' },
  ];
  
  export default {
    name: 'EmployeeDashboard',
  
    components: { Header, GChart },
  
    data() {
      return {
        loading: false,
  
        selectedMonth: 0,
        // selectedMonth: new Date().getMonth() + 1,
        months: [
        { value: 0, label: 'All Months' },
          { value: 1, label: 'January' }, { value: 2, label: 'February' },
          { value: 3, label: 'March' },   { value: 4, label: 'April' },
          { value: 5, label: 'May' },     { value: 6, label: 'June' },
          { value: 7, label: 'July' },    { value: 8, label: 'August' },
          { value: 9, label: 'September' },{ value: 10, label: 'October' },
          { value: 11, label: 'November' },{ value: 12, label: 'December' },
        ],
  
        // Raw employees list
        employees: [],
  
        // Derived
        stats: {
          active: 0, on_leave: 0, suspended: 0, terminated: 0,
          invited: 0, pending: 0, resigned: 0,
        },
        gender:      { male: 0, female: 0, other: 0 },
        birthdays:   [],
        paygrades:   [],
        departments: [],
        trend:       { labels: [], joined: [], left: [] },
  
  
        toast: useToast(),
      };
    },
  
    computed: {
      totalEmployees() {
        return Object.values(this.stats).reduce((s, v) => s + v, 0);
      },
  
      statusBars() {
        return [
          { key: 'active',      label: 'Active',      count: this.stats.active,     color: '#1D9E75' },
          { key: 'on_leave',    label: 'On leave',    count: this.stats.on_leave,   color: '#7F77DD' },
          { key: 'pending',     label: 'Pending',     count: this.stats.pending,    color: '#378ADD' },
          { key: 'invited',     label: 'Invited',     count: this.stats.invited,    color: '#EF9F27' },
          { key: 'suspended',   label: 'Suspended',   count: this.stats.suspended,  color: '#EA580C' },
          { key: 'terminated',  label: 'Terminated',  count: this.stats.terminated, color: '#E24B4A' },
          { key: 'resigned',    label: 'Resigned',    count: this.stats.resigned,   color: '#888780' },
        ];
      },
  
      trendChartData() {
        const rows = this.trend.labels.map((label, i) => [
          label,
          this.trend.joined[i],
          this.trend.left[i],
        ]);
        return [['Month', 'Joined', 'Left'], ...rows];
      },
  
      trendChartOptions() {
        return {
          legend: { position: 'none' },
          chartArea: { left: 44, right: 8, top: 8, bottom: 30, width: '100%', height: '80%' },
          bar: { groupWidth: '60%' },
          series: { 0: { color: '#378ADD' }, 1: { color: '#E24B4A' } },
          vAxis: { gridlines: { color: '#f0f4f8' }, textStyle: { color: '#7a90b0', fontSize: 11 } },
          hAxis: { textStyle: { color: '#7a90b0', fontSize: 11 } },
          backgroundColor: 'transparent',
        };
      },
  
      genderChartData() {
        return [
          ['Gender', 'Count'],
          ['Male',   this.gender.male],
          ['Female', this.gender.female],
          ['Other',  this.gender.other],
        ];
      },
  
      genderChartOptions() {
        return {
          legend: { position: 'none' },
          pieHole: 0.65,
          chartArea: { left: 0, right: 0, top: 0, bottom: 0, width: '100%', height: '100%' },
          colors: ['#378ADD', '#D4537E', '#888780'],
          backgroundColor: 'transparent',
          pieSliceBorderColor: '#fff',
        };
      },
    },
  
    created() {
      this.fetchAll();
    },
  
  
    methods: {
      // -- Main fetch ----------------------------------------------------
      async fetchAll() {
        this.loading = true;
        try {
          await Promise.all([
            this.fetchEmployees(),
            this.fetchPaygrades(),
            this.fetchDepartments(),
          ]);
        } finally {
          this.loading = false;
        }
      },
  
      async fetchEmployees() {
        const { data } = await axios.get('/payroll/employees/active', { params: { month: this.selectedMonth } });
        const list = Array.isArray(data.data?.data) ? data.data.data
          : Array.isArray(data.data) ? data.data : [];
        this.employees = list;
        this.deriveStats(list);
        this.deriveGender(list);
        this.deriveBirthdays(list);
        this.deriveTrend(list);
      },
  
      async fetchPaygrades() {
        try {
          const { data } = await axios.get('/payroll/paygrade/active', { params: { month: this.selectedMonth } });
          const list = Array.isArray(data.data) ? data.data : data.data?.data ?? [];
          // Count employees per paygrade from employees list (fallback: use grade data directly)
          const counts = {};
          this.employees.forEach(e => {
            const id = e.pay_grade_id || e.paygrade_id;
            if (id) counts[id] = (counts[id] || 0) + 1;
          });
          this.paygrades = list.map(g => ({
            id:    g.id,
            name:  g.grade_name || g.name || `Grade ${g.id}`,
            count: counts[g.id] || g.employee_count || 0,
          })).filter(g => g.count > 0)
            .sort((a, b) => b.count - a.count);
        } catch (_) { /* intentionally silent */ }
      },
  
      async fetchDepartments() {
        try {
          const { data } = await axios.get('payroll/department/active', { params: { month: this.selectedMonth } });
          const list = Array.isArray(data.data?.data) ? data.data.data
            : Array.isArray(data.data) ? data.data : [];
          const counts = {};
          this.employees.forEach(e => {
            const id = e.department_id;
            if (id) counts[id] = (counts[id] || 0) + 1;
          });
          this.departments = list.map(d => ({
            id:    d.id,
            name:  d.name,
            count: counts[d.id] || d.employee_count || 0,
          })).filter(d => d.count > 0)
            .sort((a, b) => b.count - a.count);
        } catch (_) { /* intentionally silent */ }
      },
  
      // -- Derivations ---------------------------------------------------
      deriveStats(list) {
        const s = { active: 0, on_leave: 0, suspended: 0, terminated: 0, invited: 0, pending: 0, resigned: 0 };
        list.forEach(e => {
          const st = (e.status || '').toLowerCase().replace(' ', '_');
          if (st === 'active')                      s.active++;
          else if (st === 'on-leave' || st === 'on_leave') s.on_leave++;
          else if (st === 'suspended')              s.suspended++;
          else if (st === 'terminated')             s.terminated++;
          else if (st === 'invited')                s.invited++;
          else if (st === 'pending')                s.pending++;
          else if (st === 'resigned')               s.resigned++;
          else if (e.is_active == 1)                s.active++;
          else                                      s.pending++;
        });
        this.stats = s;
      },
  
      deriveGender(list) {
        const g = { male: 0, female: 0, other: 0 };
        list.forEach(e => {
          const gen = (e.gender || '').toLowerCase();
          if (gen === 'male')        g.male++;
          else if (gen === 'female') g.female++;
          else                       g.other++;
        });
        this.gender = g;
      },
  
      deriveBirthdays(list) {
        const today = new Date();
        const todayMD = `${today.getMonth()}-${today.getDate()}`;
        const cutoff  = new Date(today);
        cutoff.setDate(today.getDate() + 30);
  
        this.birthdays = list.map((e, i) => {
          if (!e.dob) return null;
          const dob  = new Date(e.dob);
          const next = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
          if (next < today) next.setFullYear(today.getFullYear() + 1);
          if (next > cutoff) return null;
          const pal   = AVATAR_PALETTES[i % AVATAR_PALETTES.length];
          const fname = e.first_name || '';
          const lname = e.last_name  || '';
          return {
            id:          e.id,
            name:        `${fname} ${lname}`.trim() || 'Unknown',
            department:  e.department?.name || '-',
            initials:    `${fname.charAt(0)}${lname.charAt(0)}`.toUpperCase() || '?',
            avatarBg:    pal.bg,
            avatarColor: pal.color,
            isToday:     `${dob.getMonth()}-${dob.getDate()}` === todayMD,
            dateLabel:   next.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
            _sort:       next,
          };
        }).filter(Boolean).sort((a, b) => a._sort - b._sort).slice(0, 7);
      },
  
      deriveTrend(list) {
        const months = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date();
          d.setMonth(d.getMonth() - i);
          months.push({ year: d.getFullYear(), month: d.getMonth(), label: d.toLocaleDateString('en-GB', { month: 'short' }) });
        }
        const joined = months.map(m =>
          list.filter(e => {
            if (!e.hire_date) return false;
            const d = new Date(e.hire_date);
            return d.getFullYear() === m.year && d.getMonth() === m.month;
          }).length
        );
        // resigned/terminated in each month - use updated_at or termination date if available
        const left = months.map(() => 0); // fallback: no departure date field
        this.trend = { labels: months.map(m => m.label), joined, left };
      },
  
      // -- Helpers -------------------------------------------------------
      pct(count) {
        if (!this.totalEmployees) return 0;
        return Math.round((count / this.totalEmployees) * 100);
      },
  
      genderPct(key) {
        const total = this.gender.male + this.gender.female + this.gender.other;
        if (!total) return 0;
        return Math.round((this.gender[key] / total) * 100);
      },
  
      pgWidth(count) {
        const max = Math.max(...this.paygrades.map(p => p.count), 1);
        return Math.round((count / max) * 100);
      },
    },
  };
  </script>
  
  
  <style scoped>
  /* -- Page -------------------------------------------------------------- */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .mb-4 { margin-bottom:16px; }
  .mb-5 { margin-bottom:20px; }
  .mb-2 { margin-bottom:8px; }
  .mt-3 { margin-top:12px; }
  
  /* -- Buttons / links --------------------------------------------------─ */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; text-decoration:none; font-family:inherit; }
  .btn-primary-action:hover { background:#003b8e; }
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  
  /* -- Status stat cards ------------------------------------------------─ */
  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
  .stat-card { border-radius:12px; padding:14px 16px; display:flex; align-items:center; gap:12px; }
  .stat-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .stat-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; margin:0 0 3px; }
  .stat-value { font-size:22px; font-weight:800; margin:0 0 2px; line-height:1; }
  .stat-sub   { font-size:11px; margin:0; }
  
  .stat-card--green  { background:#E1F5EE; }
  .stat-icon--green  { background:#9FE1CB; color:#085041; }
  .stat-card--green  .stat-label { color:#0F6E56; }
  .stat-card--green  .stat-value { color:#04342C; }
  .stat-card--green  .stat-sub   { color:#0F6E56; }
  
  .stat-card--purple { background:#EEEDFE; }
  .stat-icon--purple { background:#CECBF6; color:#26215C; }
  .stat-card--purple .stat-label { color:#534AB7; }
  .stat-card--purple .stat-value { color:#26215C; }
  .stat-card--purple .stat-sub   { color:#534AB7; }
  
  .stat-card--amber  { background:#FAEEDA; }
  .stat-icon--amber  { background:#FAC775; color:#412402; }
  .stat-card--amber  .stat-label { color:#854F0B; }
  .stat-card--amber  .stat-value { color:#412402; }
  .stat-card--amber  .stat-sub   { color:#854F0B; }
  
  .stat-card--red    { background:#FCEBEB; }
  .stat-icon--red    { background:#F7C1C1; color:#501313; }
  .stat-card--red    .stat-label { color:#A32D2D; }
  .stat-card--red    .stat-value { color:#501313; }
  .stat-card--red    .stat-sub   { color:#A32D2D; }
  
  .stat-card--blue   { background:#E6F1FB; }
  .stat-icon--blue   { background:#B5D4F4; color:#042C53; }
  .stat-card--blue   .stat-label { color:#185FA5; }
  .stat-card--blue   .stat-value { color:#042C53; }
  .stat-card--blue   .stat-sub   { color:#185FA5; }
  
  .stat-card--gray   { background:#F1EFE8; }
  .stat-icon--gray   { background:#D3D1C7; color:#2C2C2A; }
  .stat-card--gray   .stat-label { color:#5F5E5A; }
  .stat-card--gray   .stat-value { color:#2C2C2A; }
  .stat-card--gray   .stat-sub   { color:#5F5E5A; }
  
  /* -- Layout ------------------------------------------------------------ */
  .two-col { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  
  /* -- Data card --------------------------------------------------------─ */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  .card-hd   { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #e4eaf2; }
  .card-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .card-sub   { font-size:12px; color:#7a90b0; margin:0; }
  .card-body  { padding:16px 20px; }
  
  /* -- Status bars ------------------------------------------------------─ */
  .bar-list  { display:flex; flex-direction:column; gap:10px; }
  .bar-row   { display:flex; align-items:center; gap:10px; }
  .bar-label { font-size:12px; color:#7a90b0; width:80px; flex-shrink:0; }
  .bar-track { flex:1; height:7px; border-radius:4px; background:#f0f4f8; overflow:hidden; }
  .bar-fill  { height:100%; border-radius:4px; transition:width .4s ease; }
  .bar-count { font-size:12px; font-weight:600; color:#1a2b45; width:24px; text-align:right; }
  .bar-pct   { font-size:11px; color:#7a90b0; width:32px; text-align:right; }
  
  /* -- Birthdays --------------------------------------------------------─ */
  .bday-list { display:flex; flex-direction:column; gap:8px; }
  .bday-row  { display:flex; align-items:center; gap:10px; padding:8px 10px; border-radius:8px; background:#f8fafc; }
  .emp-avatar { width:34px; height:34px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
  .bday-info  { flex:1; min-width:0; }
  .bday-name  { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 2px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .bday-dept  { font-size:11px; color:#7a90b0; margin:0; }
  .bday-date  { flex-shrink:0; }
  .bday-date-text { font-size:12px; font-weight:500; color:#7a90b0; }
  .badge        { font-size:10.5px; font-weight:700; padding:2px 8px; border-radius:20px; }
  .badge--green { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
  
  /* -- Pay grades -------------------------------------------------------- */
  /* (reuses .bar-list / .bar-row / .bar-track / .bar-fill) */
  
  /* -- Departments ------------------------------------------------------─ */
  .dept-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
  .dept-card { display:flex; align-items:center; gap:10px; background:#f8fafc; border-radius:8px; padding:10px 12px; }
  .dept-icon { width:36px; height:36px; border-radius:8px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
  .dept-info  { min-width:0; }
  .dept-name  { font-size:12px; font-weight:600; color:#1a2b45; margin:0 0 2px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .dept-count { font-size:20px; font-weight:800; color:#004AAD; margin:0; line-height:1; }
  .dept-sub   { font-size:10px; color:#7a90b0; margin:0; }
  
  /* -- Charts ------------------------------------------------------------ */
  .chart-wrap     { position:relative; height:200px; }
  .chart-wrap--sm { height:170px; width:170px; flex-shrink:0; }
  .chart-legend { display:flex; gap:14px; }
  .leg-item  { display:flex; align-items:center; gap:5px; font-size:12px; color:#7a90b0; }
  .leg-dot   { width:10px; height:10px; border-radius:2px; flex-shrink:0; }
  
  /* -- Gender ------------------------------------------------------------ */
  .gender-body  { display:flex; align-items:center; gap:1.5rem; }
  .gender-stats { display:flex; flex-direction:column; gap:14px; flex:1; }
  .gender-item  { display:flex; align-items:flex-start; gap:10px; }
  .gender-label { font-size:12px; color:#7a90b0; margin:0 0 3px; }
  .gender-val   { font-size:20px; font-weight:800; color:#1a2b45; margin:0; line-height:1; }
  .gender-pct   { font-size:12px; font-weight:400; color:#7a90b0; }
  
  /* -- Empty + loading --------------------------------------------------─ */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:80px 24px; text-align:center; }
  .empty-mini  { display:flex; flex-direction:column; align-items:center; padding:32px 0; color:#7a90b0; text-align:center; }
  .empty-icon  { font-size:28px; margin-bottom:10px; color:#c0c9d8; }
  .text-muted  { color:#7a90b0; font-size:13px; margin:0; }
  
  /* -- Responsive -------------------------------------------------------- */
  @media (max-width:1024px) {
    .stat-grid { grid-template-columns:repeat(2,1fr); }
  }
  @media (max-width:768px) {
    .two-col  { grid-template-columns:1fr; }
    .dept-grid { grid-template-columns:1fr; }
    .gender-body { flex-direction:column; align-items:flex-start; }
    .chart-wrap--sm { width:100%; }
    .page-header, .page-actions { flex-direction:column; align-items:stretch; }
  }
  @media (max-width:480px) {
    .stat-grid { grid-template-columns:1fr; }
  }
  </style>